import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import {
  Button,
  Divider,
  Dropdown,
  Menu,
  message,
  Space,
  Tooltip,
  Typography,
  Select,
  Input,
  DatePicker,
  Cascader,
} from 'antd'
import { DeleteModal, IconFont, SvgIcon } from '@/components'
import {
  useDeleteTask,
  useDeleteTaskExec,
  useGetBizAreaList,
  useGetGroupList,
  useGetHostList,
  useGetTaskDeviceList,
  useGetTaskExecList,
  useGetTaskList,
  usePauseTask,
  useStartTask,
  useStopTask,
  useChangeJobStatus,
} from './utils'
import TaskTable from '@/components/task-table/task-table'
import DetailDrawer from './detail-drawer'
import { RetestModal } from './retest-modal'
import ImportModal from './import-modal'
import { useNavigate } from 'react-router'
import { CreateReport } from '@/components/create-report'
import { useAddReport } from '@/pages/evaluation/report-task/utils'
import { Message } from '@/components/message'
import { useGetAuthorizeConfig } from '@/pages/account-center/authorize-manage/utils'
import { formatDateTime } from '@/utils/date'
import { TablePaginationConfig } from 'antd/es/table'
import { FilterValue, SorterResult } from 'antd/es/table/interface'
import debounce from 'lodash/debounce'
import { t } from '@/i18n'

const { Text } = Typography
const { RangePicker } = DatePicker

export interface ESDataType {
  //  执行次数上限
  executeTimesLimit: number
  // 已执行次数
  executeTimesUsed: number
  // 首次运行时间
  firstExecuteTime: string
  // 任务分组
  groupName: string
  // 任务id
  id: string
  // 执行类型
  kind: KindProps
  // 最近结束时间
  latestExecuteTime: string
  // 任务名称
  name: string
  // 任务综合评分
  score: number
  // 状态分布
  statusList: { status: string; count: number }[]
  // 验证类型
  verificationType: string
  // 子任务id
  subRowKeys: string[]
  // 是否可以删除
  isRemove: boolean
  // 设备
  devices: {
    deviceType: string
    deviceName: string
    deviceIp: string
    isOnline: boolean
  }[]
  // 验证模式
  verificationMode?: {
    agentToHost: { hostName: string; hostIp: string }
    agentToListener: { hostName: string; hostIp: string }
  }
}

export interface ESSubDataType {
  //  客户端
  agent: string
  //  安全域
  areaName: string
  //  用例发送数
  resourceSendTotal: number
  //  用例总数
  resourceTotal: number
  //  创建时间
  createTime: string
  // 开始时间
  startTime: string
  //  结束时间
  endTime: string
  //  任务执行id
  id: string
  //  任务评分
  score: number
  // 执行状态
  status: string
  // 离线验证任务名称
  taskName?: string
  // 是否可以删除
  isRemove: boolean
  // 设备
  devices?: {
    deviceType: string
    deviceName: string
    deviceIp: string
    isOnline: boolean
  }[]
  // 异常原因
  exceptReason?: string
}

type KindProps = 'TaskKindImmediately' | 'TaskKindBooking' | 'TaskKindCycle'

const KindMap = {
  TaskKindImmediately: t('立即'),
  TaskKindBooking: t('预定'),
  TaskKindCycle: t('周期'),
}

const VerificationTypeMap = {
  VerificationTypeOnline: { label: t('在线验证'), color: '#67C23A' },
  VerificationTypeOffline: { label: t('离线验证'), color: '#999999' },
}

const statusMap = {
  TaskStatusFinished: { label: t('已完成'), className: 'success' },
  TaskStatusRunning: { label: t('进行中'), className: 'processing' },
  TaskStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskStatusError: { label: t('错误'), className: 'error' },
  TaskStatusPausing: { label: t('停止中'), className: 'warning' },
  TaskStatusPause: { label: t('暂停'), className: 'warning' },
}

const statusResultMap = {
  TaskResultStatusFinished: { label: t('已完成'), className: 'success' },
  TaskResultStatusProcessing: { label: t('进行中'), className: 'processing' },
  TaskResultStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskResultStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskResultStatusStopping: { label: t('停止中'), className: 'warning' },
}

const TaskListMain: React.FC = () => {
  const navigate = useNavigate()
  // 获取授权配置
  const [authConfig, getAuthorizeConfig] = useGetAuthorizeConfig()
  // 获取安全域 （筛选)
  const [area, getArea] = useGetBizAreaList()
  // 获取分组列表 (筛选)
  const [getGroupList, groupList] = useGetGroupList()
  // 获取所有任务关联安全设备列表 (筛选)
  const [taskDeviceList, getTaskDeviceList] = useGetTaskDeviceList()
  // 暂停/重启任务
  const [changeJobStatus] = useChangeJobStatus()
  // 暂停主任务
  const [pauseTask] = usePauseTask()
  // 开启主任务
  const [startTask] = useStartTask()
  // 获取任务列表
  const [getTaskList, getTaskListLoading] = useGetTaskList()
  // 获取任务执行列表
  const [getTaskExecuteList, getTaskExecuteListLoading] = useGetTaskExecList()
  // 任务停止
  const [stopTask] = useStopTask()
  // 主任务删除
  const [deleteTask, deleteTaskLoading] = useDeleteTask()
  // 子任务删除
  const [deleteSubTask, deleteSubTaskLoading] = useDeleteTaskExec()
  // 创建报表
  const [createReport, createLoading] = useAddReport()
  const [searchParams, setSearchParams] = useState({ page: 1, limit: 10 })
  const [total, setTotal] = useState(0)
  const [dataSource, setDataSource] = useState<ESDataType[]>([])
  const [subDataSource, setSubDataSource] = useState<ESSubDataType[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedSubRowKeys, setSelectedSubRowKeys] = useState<string[]>([])
  const [count, setCount] = useState(1)
  const [subTotal, setSubTotal] = useState(0)
  // 任务详情抽屉
  const [view, setView] = useState<{ visible: boolean; id: string }>({ visible: false, id: '' })
  // 离线任务导入弹窗
  const [importModalVisible, setImportModalVisible] = useState(false)
  // 删除任务弹窗
  const [remove, setRemove] = useState<{ visible: boolean; id: string[]; type: string }>({
    visible: false,
    id: [],
    type: '',
  })
  // 任务复测弹窗
  let [retestVisible, setRetestVisible] = useState({
    visible: false,
    result_id: '',
    task_id: '',
    verificationType: '',
  })
  // 创建报表弹窗
  const [report, setReport] = useState<{
    visible: boolean
    id: string | string[]
    name: string
    multiple?: boolean
    taskList?: { task_name: string; campaign_id: string }[]
    selectItem?: any
  }>({ visible: false, id: '', multiple: false, name: '', taskList: [], selectItem: null })
  // 任务复测弹窗
  const [mainTaskName, setMainTaskName] = useState('')
  // 下载状态管理
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set())
  // host列表筛选框
  const [getHostList, hostList] = useGetHostList()

  const [searchType, setSearchType] = useState('search')
  const [searchValue, setSearchValue] = useState('')
  const [selectValue, setSelectValue] = useState(undefined)
  const [dateValue, setDateValue] = useState([])

  const searchOptions = useMemo(
    () => [
      { label: t('主任务名称'), value: 'search', type: 'input' },
      { label: t('子任务名称'), value: 'resultName', type: 'input' },
      {
        label: t('验证类型'),
        value: 'verificationType',
        type: 'select',
        selectData: [
          { label: t('在线验证'), value: 'VerificationTypeOnline' },
          { label: t('离线验证'), value: 'VerificationTypeOffline' },
        ],
      },
      {
        label: t('安全域'),
        value: 'areaName',
        type: 'select',
        selectData: area.map(item => ({
          label: item.name || t('默认安全域'),
          value: item.name || t('默认安全域'),
        })),
      },
      {
        label: t('分组'),
        value: 'groupId',
        type: 'select',
        selectData: groupList.map(item => ({ label: item.name, value: item.id })),
      },
      {
        label: t('安全设备'),
        value: 'deviceIds',
        type: 'select',
        selectData: taskDeviceList.map(item => ({
          label: `${item.deviceType || '-'}|${item.name || '-'}|${item.deviceIp || '-'}`,
          value: item.id,
        })),
      },
      {
        label: t('目标主体'),
        value: 'hostInfo',
        type: 'select',
        selectData: hostList.map(item => ({
          label: `${item.hostName} | ${item.hostIp}`,
          value: `${item.hostId}`,
          raw: {
            hostName: item.hostName,
            hostIp: item.hostIp,
            hostId: item.hostId,
          },
        })),
      },
      {
        label: t('任务状态'),
        value: 'taskStatus',
        type: 'select',
        selectData: [
          { label: t('等待中'), value: 'TaskResultStatusWaiting' },
          { label: t('进行中'), value: 'TaskResultStatusProcessing' },
          { label: t('已完成'), value: 'TaskResultStatusFinished' },
          { label: t('中断'), value: 'TaskResultStatusInterrupted' },
          { label: t('停止中'), value: 'TaskResultStatusStopping' },
        ],
      },
      { label: t('创建日期'), value: 'time', type: 'date' },
    ],

    [area, groupList, taskDeviceList, hostList],
  )

  const debouncedInputSearch = useMemo(
    () =>
      debounce((type, value) => {
        setSearchParams(prev => ({ ...prev, [type]: value, page: 1 }))
      }, 400),
    [setSearchParams],
  )

  useEffect(
    () => () => {
      debouncedInputSearch.cancel()
    },
    [debouncedInputSearch],
  )

  const buildItems = ({ label }, id, isRemove) => {
    const items = [
      {
        key: 'copy',
        label: (
          <div className="copy-btn" onClick={() => copyData(id)}>
            <span className="text">{t('任务复制')}</span>
          </div>
        ),
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && removeData(id, 'main')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),
      },
    ]

    const isDownloading = downloadingIds.has(id)
    const item = {
      key: 'download',
      label: (
        <div
          className={isDownloading ? 'download-btn-disabled' : 'download-btn'}
          onClick={() => !isDownloading && downloadData(id)}
        >
          <span className="text">{isDownloading ? t('下载中...') : t('下载离线包')}</span>
        </div>
      ),

      disabled: isDownloading,
    }

    if (label !== t('离线验证')) return <Menu items={items} />
    items.splice(1, 0, item)

    return <Menu items={items} />
  }

  const buildSubItems = (
    taskName: string,
    id: string,
    taskId: string,
    verificationType: string,
    isReport: boolean,
    isRemove: boolean,
    isRetest: boolean,
  ) => {
    const items = [
      {
        key: 'report',
        label: (
          <div
            className={isReport ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => isReport && reportData(id, taskName)}
          >
            <span className="text">{t('生成报表')}</span>
          </div>
        ),

        disabled: !isReport,
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && removeData(id, 'sub')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),

        disabled: !isRemove,
      },
    ]

    const item = {
      key: 'retest',
      label: (
        <div
          className={isRetest ? 'download-btn' : 'download-btn-disabled'}
          onClick={() =>
            isRetest &&
            setRetestVisible({ visible: true, result_id: id, task_id: taskId, verificationType })
          }
        >
          <span className="text">{t('任务复测')}</span>
        </div>
      ),

      disabled: !isRetest,
    }

    if (verificationType === 'VerificationTypeOnline') {
      items.splice(1, 0, item)
    }

    return <Menu items={items} />
  }

  const columns = [
    {
      title: t('任务名称'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left' as const,
      render: (name, { devices }) => {
        const hasDevice = devices?.length > 0
        const hasOnlineDevice = devices?.some(device => device.isOnline)
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text
              ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
              style={{ maxWidth: 150 }}
            >
              {name}
            </Text>
            {hasDevice && (
              <Tooltip
                overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
                title={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      color: '#FFFFFF',
                      lineHeight: '20px',
                      maxWidth: 'min(800px, 90vw)',
                    }}
                  >
                    <div>{t('设备清单')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {devices.map(device => (
                        <div
                          key={device.deviceName}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            width: '100%',
                          }}
                        >
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: '1 1 auto',
                              minWidth: 0,
                            }}
                          >
                            {device.deviceType} | {device.deviceName} | {device.deviceIp}
                          </span>
                          <span
                            style={{
                              marginLeft: 12,
                              marginRight: 4,
                              width: 6,
                              height: 6,
                              background: device.isOnline ? '#67C23A' : '#677280',
                              borderRadius: '50%',
                              flexShrink: 0,
                            }}
                          />

                          <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {device.isOnline ? t('在线') : t('离线')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                }
                getPopupContainer={() => document.body}
              >
                <SvgIcon
                  name={hasOnlineDevice ? t('设备') : t('设备灰')}
                  width="12px"
                  height="12px"
                  style={{ marginRight: 4 }}
                />
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: t('任务综合评分'),
      dataIndex: 'score',
      key: 'score',
      width: 120,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: ({
        score,
        riskLevel,
        className,
      }: {
        score: number
        riskLevel: string
        className: string
      }) => (
        <span>
          <span className="score">{score}</span>
          <span className="divider">|</span>
          <span className={className}>{riskLevel}</span>
        </span>
      ),
    },
    {
      title: t('运行状态分布'),
      dataIndex: 'statusList',
      key: 'statusList',
      width: 200,
      render: statusList => (
        <div style={{ display: 'flex' }}>
          {statusList?.map(status => (
            <div key={status.name} className={`status ${status.className}`}>
              <span>{status.name}</span>
              <span className="count">{status.count}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('目标主体'),
      dataIndex: 'verificationMode',
      key: 'verificationMode',
      width: 150,
      render: (verificationMode: {
        agentToHost: { hostName: string; hostIp: string }
        agentToListener: { hostName: string; hostIp: string }
      }) => (
        <Tooltip
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ flexShrink: 0 }}>
                  {verificationMode.agentToListener.hostName === ''
                    ? ''
                    : verificationMode.agentToListener.hostName ||
                      verificationMode.agentToHost.hostName}
                </span>
                <span style={{ margin: '0 4px', color: '#677280' }}>
                  {' '}
                  {verificationMode.agentToListener.hostName === '' ? '' : '|'}
                </span>
                <span style={{ flexShrink: 0 }}>
                  {verificationMode.agentToListener.hostIp === ''
                    ? ''
                    : verificationMode.agentToListener.hostIp ||
                      verificationMode.agentToHost.hostIp}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ flexShrink: 0 }}>
                  {verificationMode.agentToHost.hostName === ''
                    ? ''
                    : verificationMode.agentToHost.hostName ||
                      verificationMode.agentToListener.hostName}
                </span>
                <span style={{ margin: '0 4px', color: '#677280' }}>
                  {verificationMode.agentToHost.hostName === '' ? '' : '|'}
                </span>
                <span style={{ flexShrink: 0 }}>
                  {verificationMode.agentToHost.hostIp === ''
                    ? ''
                    : verificationMode.agentToHost.hostIp ||
                      verificationMode.agentToListener.hostIp}
                </span>
              </div>
            </div>
          }
          overlayStyle={{
            maxWidth: '400px', // 增加Tooltip的最大宽度
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>
              <Text ellipsis style={{ maxWidth: 60 }}>
                {verificationMode.agentToHost.hostName || verificationMode.agentToListener.hostName}
              </Text>
            </span>
            <span style={{ margin: '0 4px', color: '#677280' }}>|</span>
            <span>
              <Text ellipsis style={{ maxWidth: 60 }}>
                {verificationMode.agentToHost.hostIp || verificationMode.agentToListener.hostIp}
              </Text>
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('执行类型'),
      dataIndex: 'kind',
      key: 'kind',
      width: 100,
      render: (kind: string, { executeTimesUsed, executeTimesLimit }: ESDataType) => (
        <div
          className={
            [t('立即'), t('预定')].includes(kind) ? 'kind-immediately kind-wrapper' : 'kind-wrapper'
          }
        >
          <span className="kind">{kind}</span>
          {![t('立即'), t('预定')].includes(kind) && (
            <>
              <span>{executeTimesUsed || 0}</span>
              <span className="divider">|</span>
              <span>{executeTimesLimit === 0 ? '∞' : executeTimesLimit}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('任务优先级'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (priority: string) => <span>{priority}</span>,
    },
    {
      title: t('分组'),
      dataIndex: 'groupName',
      key: 'groupName',
      width: 80,
      ellipsis: true,
    },
    {
      title: t('验证类型'),
      dataIndex: 'verificationType',
      key: 'verificationType',
      width: 90,
      render: ({ label }) => (
        <div>
          <span style={{ marginLeft: 4 }}>{label}</span>
        </div>
      ),
    },
    {
      title: t('首次运行时间'),
      dataIndex: 'firstExecuteTime',
      key: 'firstExecuteTime',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text === '-' ? '-' : formatDateTime(text, 'standard')}>
          {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
        </Tooltip>
      ),
    },
    {
      title: t('最近结束时间'),
      dataIndex: 'latestExecuteTime',
      key: 'latestExecuteTime',
      width: 120,
      render: (text: string) => (
        <Tooltip title={text === '-' ? '-' : formatDateTime(text, 'standard')}>
          {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, { id, verificationType, taskStatus, exceptReason, isRemove }) => (
        <Space size="middle">
          {taskStatus && taskStatus !== t('已完成') && (
            <div className="stop-btn">
              {[t('等待中'), t('中断'), t('暂停'), t('错误')].includes(taskStatus) && (
                <span
                  onClick={() => {
                    if (taskStatus === t('中断') || taskStatus === t('错误')) {
                      message.warn({
                        className: 'task-warn',
                        content: (
                          <div>
                            <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
                            {t('异常原因：')}
                            {exceptReason}
                          </div>
                        ),
                      })
                      return
                    }
                    handleOpenTask(id, 2, verificationType)
                  }}
                >
                  <span className="text">{t('开启')}</span>
                </span>
              )}
              {[t('进行中')].includes(taskStatus) && (
                <span onClick={() => handleOpenTask(id, 1, verificationType)}>
                  <span className="text">{t('暂停')}</span>
                </span>
              )}
            </div>
          )}
          <div className="view-btn" onClick={() => viewData(id)}>
            <span className="text">{t('查看')}</span>
          </div>
          <Dropdown
            overlay={buildItems(verificationType, id, isRemove)}
            getPopupContainer={() => document.body}
          >
            <span>
              {t('更多')} <DownOutlined />
            </span>
          </Dropdown>
        </Space>
      ),
    },
  ]

  const subColumns = useMemo(() => {
    const arr = [
      {
        title: t('任务评分'),
        dataIndex: 'score',
        key: 'score',
        width: '100px',
        render: ({
          score,
          riskLevel,
          className,
        }: {
          score: number
          riskLevel: string
          className: string
        }) => (
          <span>
            <span className="score">{score}</span>
            <span className="divider">|</span>
            <span className={className}>{riskLevel}</span>
          </span>
        ),
      },
      {
        title: t('运行状态'),
        dataIndex: 'status',
        key: 'status',
        width: 200,
        render: (status, { resourceSendTotal, resourceTotal, exceptReason }) => (
          <div className="status-wrapper">
            <div>
              <span>{resourceSendTotal || 0}</span>
              <span className="divider">|</span>
              <span>{resourceTotal}</span>
            </div>
            <span className={`status ${status?.className}`}>{status?.label}</span>
            {status?.label === t('中断') && (
              <Tooltip title={exceptReason} getPopupContainer={() => document.body}>
                <IconFont
                  type={'icon-jinggao_jinggao'}
                  style={{ color: '#F53F3F', fontSize: '14px' }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: t('创建时间'),
        dataIndex: 'createTime',
        key: 'createTime',
        width: 120,
        render: (text: string) => (
          <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
        ),
      },
      {
        title: t('开始时间'),
        dataIndex: 'startTime',
        key: 'startTime',
        width: 120,
        render: (text: string) => (
          <Tooltip title={text.length === 1 ? '-' : formatDateTime(text, 'standard')}>
            {text.length === 1 ? '-' : formatDateTime(text, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('结束时间'),
        dataIndex: 'endTime',
        key: 'endTime',
        width: 120,
        render: (text: string) => (
          <Tooltip title={text.length === 1 ? '-' : formatDateTime(text, 'standard')}>
            {text.length === 1 ? '-' : formatDateTime(text, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('操作'),
        key: 'action',
        width: 150,
        fixed: 'right',
        render: (
          _,
          { taskName, taskId, id, verificationType, isStop, isView, isReport, isRetest, isRemove },
        ) => (
          <Space size="middle">
            {isStop && (
              <div
                onClick={() => handleStopTask(id, taskId, verificationType)}
                className="stop-btn"
              >
                <span style={{ marginLeft: 6 }}>{t('停止')}</span>
              </div>
            )}
            <div
              className={isView ? 'view-btn' : 'view-btn-disabled'}
              onClick={() =>
                isView &&
                window.open(
                  `/validation/network/endpointsecurity?tab=task&name=detail&id=${id}&task_id=${taskId}`,
                  '_blank',
                )
              }
            >
              <span className="text">{t('查看')}</span>
            </div>
            <Dropdown
              overlay={buildSubItems(
                taskName,
                id,
                taskId,
                verificationType,
                isReport,
                isRemove,
                isRetest,
              )}
              getPopupContainer={() => document.body}
            >
              <span>
                {t('更多')} <DownOutlined />
              </span>
            </Dropdown>
          </Space>
        ),
      },
    ] as any

    if (subDataSource[0]?.taskName) {
      arr.unshift({
        title: t('子任务名称'),
        dataIndex: 'taskName',
        key: 'taskName',
        width: 180,
        fixed: 'left' as const,
        render: (name, { devices }) => {
          const hasDevice = devices && devices.length > 0
          const hasOnlineDevice = devices?.some(device => device.isOnline)
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 150 }}>
                {name}
              </Text>
              {hasDevice && (
                <Tooltip
                  overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
                  title={
                    <span style={{ maxWidth: 'min(800px, 90vw)', display: 'inline-block' }}>
                      {devices[0].deviceType} | {devices[0].deviceName} | {devices[0].deviceIp}
                      <span
                        style={{
                          display: 'inline-block',
                          marginLeft: 12,
                          marginRight: 4,
                          width: 6,
                          height: 6,
                          background: devices[0].isOnline ? '#67C23A' : '#677280',
                          borderRadius: '50%',
                        }}
                      />
                      <span>{devices[0].isOnline ? t('在线') : t('离线')}</span>
                    </span>
                  }
                  getPopupContainer={() => document.body}
                >
                  <SvgIcon
                    name={hasOnlineDevice ? t('设备') : t('设备灰')}
                    width="12px"
                    height="12px"
                    style={{ marginRight: 4 }}
                  />
                </Tooltip>
              )}
            </div>
          )
        },
      })
    }

    return arr
  }, [subDataSource])

  // 查看任务详情
  function viewData(id: string) {
    setView({ visible: true, id })
  }

  // 关闭任务详情
  function closeView() {
    setView({ visible: false, id: '' })
  }

  // 打开删除弹窗
  function removeData(id: string | string[], type: string) {
    if (typeof id === 'string') {
      setRemove({ visible: true, id: [id], type })
    } else {
      setRemove({ visible: true, id, type })
    }
  }

  function copyData(id: string) {
    navigate('/validation/network/endpointsecurity?tab=create', {
      state: { id },
    })
  }

  function reportData(id: string, name: string) {
    const taskList = [{ task_name: name, campaign_id: id }]
    setReport({ visible: true, id, name, taskList })
  }

  function closeReport() {
    setReport({ visible: false, id: '', name: '' })
  }

  // 下载离线包
  function downloadData(id: string) {
    // 防止暴力点击
    if (downloadingIds.has(id)) {
      return
    }

    // 设置下载状态
    setDownloadingIds(prev => new Set(prev).add(id))

    const aLink = document.createElement('a')
    aLink.style.display = 'none'
    aLink.target = '_blank'
    aLink.href = `${
      process.env.NODE_ENV === 'dev' ? '/api' : ''
    }/portal/v1/endpoint_security/task/${id}/download_offline_package`
    document.body?.appendChild(aLink)
    aLink.click()
    document.body?.removeChild(aLink)

    // 1秒后重置下载状态，防止暴力点击
    setTimeout(() => {
      setDownloadingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }, 1000)
  }

  // 停止子任务
  async function handleStopTask(id: string, taskId: string, verificationType: string) {
    try {
      await stopTask({ resultId: id })
      initTableData(taskId, verificationType, 1, true)
      Message.success({ content: t('任务停止成功'), className: 'task-success' })
    } catch (error) {
      console.log(error)
    }
  }

  // 计划任务暂停/启动
  async function handleOpenTask(id: string, taskStatus: number, verificationType: string) {
    try {
      if (taskStatus === 1) {
        await pauseTask(id)
      } else {
        await startTask(id)
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      taskStatus === 1
        ? Message.error({ content: t('任务暂停'), className: 'task-error' })
        : Message.info({ content: t('任务启动'), className: 'task-info' })
      loadTableData(id, verificationType)
    } catch (error: any) {
      if (error.code === 400) {
        message.warn({
          className: 'task-warn',
          content: (
            <span>
              <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
              <div>
                {t('异常原因：')}
                {error.msg}
              </div>
            </span>
          ),
        })
      }
    }
  }

  /**
   * 删除任务
   * @param type 类型，main：主任务，sub：子任务 all：全部
   */
  const handleDelete = async () => {
    try {
      if (remove.type === 'main') {
        await deleteTask(remove.id)
      } else if (remove.type === 'sub') {
        await deleteSubTask({ result_id_list: remove.id })
      } else {
        await deleteTask(selectedRowKeys)
        if (selectedSubRowKeys.length > 0) {
          await deleteSubTask({ result_id_list: selectedSubRowKeys })
        }
      }
      setRemove({ visible: false, id: [], type: '' })
      setSelectedRowKeys([])
      setSelectedSubRowKeys([])
      setSearchParams({ ...searchParams, page: 1, limit: 10 })
    } catch (error) {
      console.log(error)
    }
  }

  // 展开事件
  const handleExpand = ({ expanded, record }) => {
    if (expanded) {
      loadTableData(record.id, record.verificationType)
      setMainTaskName(record.name)
      setExpandedRowKeys([record.key])
      setCount(1)
    } else {
      setExpandedRowKeys([])
      setSubDataSource([])
      setSelectedSubRowKeys([])
      setCount(1)
    }
  }

  // 创建报表
  const confirmAddGroup = values => {
    let taskData: { task_name: string; campaign_id: number }[] = []
    if (report.multiple) {
      taskData = values.campaign_ids.reduce((acc, id) => {
        const item = report.selectItem.find(i => i.id === id)
        if (item) {
          acc.push({ task_name: item.task_name, campaign_id: item.id })
        }
        return acc
      }, [])
    } else {
      if (report.taskList) {
        taskData = report.taskList
      }
    }

    createReport({
      task_list: taskData,
      report_name: values.report_name,
      template_type:
        report?.multiple && report?.selectItem?.length > 1 ? [2] : values.template_type,
      campaign_type: values.campaign_type,
      emails: '',
      assets: 0,
      include_device_logs: values.include_device_logs,
    })
      .then(() => {
        window.open('/report/task/task-report?tab=9', '_self')
      })
      .catch(_ => {})
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ESDataType> | SorterResult<ESDataType>[],
  ) => {
    let order = 'SortOrderUnspecified'
    let field: string | undefined = undefined

    if (!Array.isArray(sorter)) {
      order =
        sorter.order === 'descend'
          ? 'SORT_ORDER_DESCENDING'
          : sorter.order === 'ascend'
          ? 'SORT_ORDER_ASCENDING'
          : 'SORT_ORDER_UNSPECIFIED'
      field =
        sorter.field === 'score'
          ? 'sort_field_score'
          : sorter.field === 'priority'
          ? 'sort_field_priority'
          : sorter.field === 'latestExecuteTime'
          ? 'sort_field_update_time'
          : undefined
    }

    setSearchParams(prev => ({
      ...prev,
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      ...filters,
      sortOrder: order,
      sortField: field,
    }))
  }

  const handleSubTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log(pagination, filters, sorter)
  }

  // 构建主任务数据
  const buildDataSource = (data, taskId: string, subRowKeys: string[], isOrigin?: boolean) => {
    if (!data || data.length === 0) return []

    const arr = JSON.parse(JSON.stringify(data))
    const tableData: ESDataType[] = []

    // 仅是更新 subRowKeys
    if (isOrigin) {
      for (let i = 0; i < arr.length; i++) {
        let item = arr[i]
        if (item.id === taskId) {
          item.subRowKeys = subRowKeys
        }

        tableData.push(item)
      }

      return tableData
    }

    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      const {
        firstExecuteTime,
        latestExecuteTime,
        id,
        kind,
        statusList,
        taskStatus,
        verificationType,
        exceptReason,
        devices,
        verificationMode,
        priority,
      } = item
      // 中断的状态是否是唯一的
      const isUnique = !statusList.some(status => status.status !== 'TaskResultStatusInterrupted')
      const score = (
        item.score === 0
          ? isUnique
            ? '-'
            : 0
          : item.score === 10000
          ? 100
          : (item.score / 100).toFixed(2) || 0
      ) as number
      item.firstExecuteTime = firstExecuteTime
        ? new Date(firstExecuteTime).toLocaleString().replace(/\//g, '-')
        : '-'
      item.latestExecuteTime = latestExecuteTime
        ? new Date(latestExecuteTime).toLocaleString().replace(/\//g, '-')
        : '-'
      item.key = id
      item.kind = KindMap[kind]
      item.score = {
        score,
        riskLevel: isUnique
          ? '-'
          : (score <= 100 && score >= 80 && t('优秀')) ||
            (score < 80 && score >= 60 && t('良好')) ||
            (score < 60 && score >= 40 && t('一般')) ||
            (score < 40 && score >= 20 && t('较弱')) ||
            t('弱'),

        className:
          (score <= 100 && score >= 80 && 'low-risk') ||
          (score < 80 && score >= 60 && 'medium-risk') ||
          (score < 60 && score >= 40 && 'high-risk') ||
          (score < 40 && score >= 20 && 'weaker-risk') ||
          'serious-risk',
      }
      item.verificationType = {
        label: VerificationTypeMap[verificationType]?.label,
        color: VerificationTypeMap[verificationType]?.color,
      }
      item.statusList = statusList?.map(status => ({
        name: statusResultMap[status?.status]?.label,
        count: status?.count,
        className: statusResultMap[status?.status]?.className,
      }))
      item.isRemove = statusList.find(status =>
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status.status),
      )
        ? false
        : true

      item.taskStatus = kind === 'TaskKindCycle' ? statusMap[taskStatus]?.label : undefined
      item.exceptReason = exceptReason ? exceptReason : undefined
      item.devices = devices ? devices : undefined
      item.verificationMode = {
        agentToHost:
          verificationMode?.agentToHost && Object.keys(verificationMode.agentToHost).length > 0
            ? verificationMode.agentToHost
            : {
                hostName: t('未配置01'),
                hostIp: '127.0.0.1',
              },
        agentToListener:
          verificationMode?.agentToListener &&
          Object.keys(verificationMode.agentToListener).length > 0
            ? verificationMode.agentToListener
            : {
                hostName: t('未配置02'),
                hostIp: '127.0.0.1',
              },
      }
      item.priority = priority || '-'

      if (item.id === taskId) {
        item.subRowKeys = subRowKeys
      }

      tableData.push(item)
    }

    return tableData
  }

  // 构建子任务数据
  const buildSubDataSource = (
    data,
    taskId: string,
    verificationType: string | { label: string; color: string },
  ) => {
    if (!data || data.length === 0) return []

    const arr = JSON.parse(JSON.stringify(data))
    const subTableData: ESSubDataType[] = []
    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      const { id, taskName, status, createTime, startTime, endTime, resourceSendTotal, devices } =
        item
      const score = (
        item.score === 0
          ? status !== 'TaskResultStatusFinished'
            ? '-'
            : 0
          : item.score === 10000
          ? 100
          : (item.score / 100).toFixed(2) || 0
      ) as number
      item.key = id
      item.taskId = taskId
      item.taskName = taskName
      item.score = {
        score,
        riskLevel:
          status !== 'TaskResultStatusFinished'
            ? '-'
            : (score <= 100 && score >= 80 && t('优秀')) ||
              (score < 80 && score >= 60 && t('良好')) ||
              (score < 60 && score >= 40 && t('一般')) ||
              (score < 40 && score >= 20 && t('较弱')) ||
              t('弱'),

        className:
          (score <= 100 && score >= 80 && 'low-risk') ||
          (score < 80 && score >= 60 && 'medium-risk') ||
          (score < 60 && score >= 40 && 'high-risk') ||
          (score < 40 && score >= 20 && 'weaker-risk') ||
          'serious-risk',
      }
      item.status = statusResultMap[status]
      item.createTime = new Date(createTime).toLocaleString().replace(/\//g, '-')
      item.startTime = startTime ? new Date(startTime).toLocaleString().replace(/\//g, '-') : '-'
      item.endTime = endTime ? new Date(endTime).toLocaleString().replace(/\//g, '-') : '-'
      item.isReport = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status) ||
        ([
          'TaskResultStatusProcessing',
          'TaskResultStatusInterrupted',
          'TaskResultStatusStopping',
        ].includes(status) &&
          resourceSendTotal === 0)
      )

      item.isRetest = ![
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
      ].includes(status)
      item.isRemove = ![
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
      ].includes(status)
      item.isView = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusInterrupted',
          'TaskResultStatusStopping',
        ].includes(status) && resourceSendTotal === 0
      )

      item.isStop = ['TaskResultStatusWaiting', 'TaskResultStatusProcessing'].includes(status)
      item.verificationType =
        typeof verificationType === 'string'
          ? verificationType
          : verificationType?.label === t('在线验证')
          ? 'VerificationTypeOnline'
          : 'VerificationTypeOffline'
      item.devices = devices ? devices : undefined

      subTableData.push(item)
    }

    return subTableData
  }

  // 初始化表格数据
  const initTableData = async (
    taskId?: string,
    verificationType?: string,
    page?: number,
    onlyRefresh?: boolean,
  ) => {
    try {
      const res = await getTaskList(searchParams)
      setTotal(res.data.total)
      if (res.data.total === 0) return setDataSource([])
      const resp = await getTaskExecuteList(taskId || res.data.list?.[0]?.id, {
        ...searchParams,
        page: onlyRefresh ? 1 : page || count,
      })
      setSubTotal(resp.data.total)
      if (!mainTaskName) {
        setMainTaskName(res.data.list?.[0]?.name)
      }
      setSubDataSource(
        buildSubDataSource(
          resp.data.list,
          taskId || res.data.list?.[0]?.id,
          verificationType || res.data.list?.[0]?.verificationType,
        ),
      )
      const subRowKeys = buildSubDataSource(
        resp.data.list,
        taskId || res.data.list?.[0]?.id,
        verificationType || res.data.list?.[0]?.verificationType,
      )
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, taskId || res.data.list?.[0]?.id, subRowKeys))

      if (onlyRefresh) return
      if (resp.data.list?.length > 0) {
        setExpandedRowKeys([res.data.list?.[0]?.id])
      } else {
        setExpandedRowKeys([])
      }
    } catch (error) {
      console.log(error)
    }
  }

  // 加载表格数据
  const loadTableData = async (id: string, verificationType: string) => {
    try {
      const res = await getTaskList(searchParams)
      setTotal(res.data.total)
      let resp
      if (count !== 1) {
        resp = await getTaskExecuteList(id, { ...searchParams, page: 1 })
      } else {
        resp = await getTaskExecuteList(id, { ...searchParams, page: count })
      }
      setSubTotal(resp.data.total)
      setSubDataSource(buildSubDataSource(resp.data.list, id, verificationType))
      const subRowKeys = buildSubDataSource(resp.data.list, id, verificationType)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, id, subRowKeys))
      setExpandedRowKeys([id])
    } catch (error) {
      console.log(error)
    }
  }

  // 加载更多子任务数据
  const loadMoreSubData = async (id: string, verificationType: string) => {
    setCount(origin => origin + 1)
    const resp = await getTaskExecuteList(id, { ...searchParams, page: count + 1 })
    setSubDataSource(origin => [
      ...origin,
      ...buildSubDataSource(resp.data.list, id, verificationType),
    ])
    const subRowKeys = buildSubDataSource(resp.data.list, id, verificationType)
      .filter(item => item.isRemove)
      .map(v => v.id)
    setDataSource(origin => [...buildDataSource(origin, id, subRowKeys, true)])
  }

  useEffect(() => {
    initTableData()

    return () => {
      setTotal(0)
      setDataSource([])
      setSubDataSource([])
    }
  }, [searchParams])

  useEffect(() => {
    getArea().catch(_ => {})
    getGroupList().catch(_ => {})
    getTaskDeviceList().catch(_ => {})
    getAuthorizeConfig().catch(_ => {})
    getHostList().catch(_ => {})
  }, [])

  const batchOperation = (
    <Space split={<Divider type={'vertical'} />}>
      <div>{t('批量操作')}</div>
      <div className="batch-delete-btn" onClick={() => removeData([], 'all')}>
        <SvgIcon name={'batch_remove'} className="icon-delete" />
        <span>{t('批量删除')}</span>
      </div>
      {selectedRowKeys.length > 1 ? (
        <></>
      ) : (
        selectedSubRowKeys.length > 0 && (
          <div
            className={
              selectedSubRowKeys.length > 5 ? 'batch-report-btn disabled' : 'batch-report-btn'
            }
            onClick={() => {
              if (selectedSubRowKeys.length > 5) return
              const selectItem = [
                ...new Map(
                  subDataSource
                    .filter(item => selectedSubRowKeys.includes(item.id))
                    .map(v => [
                      v.id,
                      {
                        ...v,
                        task_name: v.taskName,
                        result_status:
                          v.status?.label === t('已完成')
                            ? 3
                            : v.status?.label === t('中断')
                            ? 4
                            : 7,
                        executed_plugin_num: v.resourceSendTotal,
                      },
                    ]),
                ).values(),
              ]

              setReport({
                visible: true,
                multiple: true,
                name: '',
                id: selectedSubRowKeys,
                selectItem,
              })
            }}
          >
            <SvgIcon
              name={selectedSubRowKeys.length > 0 ? t('批量生成报表') : 'batch_remove_ash'}
              className="icon-export"
            />

            <span>{t('批量生成报表')}</span>
          </div>
        )
      )}
    </Space>
  )

  return (
    <Wrapper>
      {/* 标题 */}
      <div className="title-wrapper">
        <div className="title">
          <span>{t('任务列表')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input.Group compact>
            <Select
              getPopupContainer={() => document.body}
              value={searchType}
              options={searchOptions}
              style={{ width: 120 }}
              virtual={false}
              onChange={v => {
                setSearchType(v)
                setSearchValue('')
                setSelectValue(undefined)
                setDateValue([])
                setSearchParams({ page: 1, limit: 10 })
              }}
            />

            {searchOptions.find(opt => opt.value === searchType)?.type === 'input' && (
              <Input
                value={searchValue}
                onChange={e => {
                  setSearchValue(e.target.value)
                  debouncedInputSearch(searchType, e.target.value)
                }}
                style={{ width: 250 }}
                placeholder={t('请输入')}
                allowClear
              />
            )}
            {searchOptions.find(opt => opt.value === searchType)?.type === 'select' && (
              <Select
                value={selectValue}
                onChange={(v, selectedOptions) => {
                  setSelectValue(v)
                  if (searchType === 'hostInfo') {
                    setSearchParams(prev => ({
                      ...prev,
                      'hostInfo.hostName': selectedOptions?.raw?.hostName,
                      'hostInfo.hostIp': selectedOptions?.raw?.hostIp,
                      'hostInfo.hostId': selectedOptions?.raw?.hostId,
                      page: 1,
                    }))
                  } else {
                    setSearchParams(prev => ({
                      ...prev,
                      [searchType]: v,
                      page: 1,
                    }))
                  }
                }}
                style={{ width: 250 }}
                placeholder={t('请选择')}
                allowClear
                showSearch
                options={searchOptions.find(opt => opt.value === searchType)?.selectData}
                filterOption={(input, option) => {
                  const label = option?.label?.toString() || ''
                  const value = option?.value?.toString() || ''
                  const searchText = input.toLowerCase()
                  return (
                    label.toLowerCase().includes(searchText) ||
                    value.toLowerCase().includes(searchText)
                  )
                }}
                virtual={false}
                getPopupContainer={() => document.body}
                notFoundContent={t('暂无匹配选项')}
              />
            )}
            {searchOptions.find(opt => opt.value === searchType)?.type === 'date' && (
              <RangePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                value={dateValue}
                onChange={v => {
                  setDateValue(v)
                  setSearchParams(prev => ({
                    ...prev,
                    createTimeStart: v?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
                    createTimeEnd: v?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
                    page: 1,
                  }))
                }}
                style={{ width: 250 }}
              />
            )}
          </Input.Group>
          {authConfig?.offlineVerification === 'On' && (
            <Button
              type="primary"
              style={{ marginRight: '8px' }}
              onClick={() => setImportModalVisible(true)}
            >
              {t('导入离线任务')}
            </Button>
          )}
          <Button
            icon={<SyncOutlined style={{ color: '#666' }} />}
            className="reset-btn"
            onClick={() => {
              initTableData(expandedRowKeys[0], '', 1, true)
              setCount(1)
            }}
          />
        </div>
      </div>
      {/* 任务列表 */}
      <div className="table-wrapper">
        <TaskTable
          batchOperation={batchOperation}
          columns={columns}
          subColumns={subColumns}
          dataSource={dataSource}
          subDataSource={subDataSource}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          total={total}
          loading={getTaskListLoading}
          hasMore={subTotal > searchParams.limit * count}
          loadMoreSubData={loadMoreSubData}
          subLoading={getTaskExecuteListLoading}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedSubRowKeys={selectedSubRowKeys}
          setSelectedSubRowKeys={setSelectedSubRowKeys}
          expandedRowKeys={expandedRowKeys}
          expandHandler={handleExpand}
          onTableChange={handleTableChange}
          onSubTableChange={handleSubTableChange}
        />
      </div>
      {/* 详情抽屉 */}
      <DetailDrawer open={view.visible} onClose={closeView} id={view.id} />
      {/* 导入离线任务弹窗 */}
      <ImportModal
        open={importModalVisible}
        initTableData={initTableData}
        onCancel={() => setImportModalVisible(false)}
      />

      {/* 删除任务弹窗 */}
      <DeleteModal
        text={
          remove.type === 'main'
            ? t('是否确认删除所选任务（删除主任务时将同时删除其下的子任务）？')
            : t('此操作将永久删除，是否继续？')
        }
        open={remove.visible}
        onOk={() => handleDelete()}
        onCancel={() => setRemove(origin => ({ ...origin, visible: false, type: '' }))}
        loading={
          (remove.type === 'main' && deleteTaskLoading) ||
          (remove.type === 'sub' && deleteSubTaskLoading)
        }
      />

      {/* 创建报表弹窗 */}
      <CreateReport
        visible={report.visible}
        multiple={report.multiple}
        selectItem={report.selectItem}
        createLoading={createLoading}
        templateType={9}
        onConfirm={e => {
          confirmAddGroup(e)
        }}
        onCancel={closeReport}
      />

      {/* 任务复测弹窗 */}
      <RetestModal
        visible={retestVisible.visible}
        onCancel={() => {
          setRetestVisible({ visible: false, result_id: '', task_id: '', verificationType: '' })
        }}
        onSuccess={() => {
          setRetestVisible(origin => ({ ...origin, visible: false }))
          initTableData(retestVisible.task_id, retestVisible.verificationType, 1, true)
          setCount(1)
        }}
        id={retestVisible.result_id}
        name={mainTaskName}
      />
    </Wrapper>
  )
}

export default TaskListMain

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
  border-radius: 8px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    margin-bottom: 6px;

    .title {
      font-weight: 600;
      font-size: 16px;
    }

    .reset-btn {
      width: 32px;
      height: 32px;
      background: #f1f3f8;
      border-radius: 6px;
      border: none;

      &:hover {
        background: #e6e9f0;
      }
    }
  }

  .table-wrapper {
    padding: 0 20px;
    overflow-y: scroll;

    .ant-dropdown-trigger {
      color: #4238c9;
    }
  }
`
